import { CreateCheckDepositScanner } from '../../Add';
import { CheckDepositRequest } from '~/typings/API';
import { mergeAccountAndCheckNumber } from '~/util/getAccountAndCheckNumber';
import { getFormattedDate } from '~/util/getDateFormat';

export const detectDuplicateChecks = (
  checks: CreateCheckDepositScanner[]
): { uniqueChecks: CreateCheckDepositScanner[]; duplicateItemIds: Set<string> } => {
  const seenChecks = new Set<string>();
  const uniqueChecks: CreateCheckDepositScanner[] = [];
  const duplicateItemIds = new Set<string>();

  checks.forEach((check) => {
    const checkKey = JSON.stringify({
      accountNumberId: check.accountNumberId,
      depositedAmount: check.depositedAmount,
      checkNumber: check.checkNumber,
      bankAccountId: check.bankAccountId,
    });

    if (seenChecks.has(checkKey)) {
      if (check.itemId !== undefined) {
        duplicateItemIds.add(String(check.itemId));
      }
    } else {
      seenChecks.add(checkKey);
      uniqueChecks.push(check);
    }
  });

  return { uniqueChecks, duplicateItemIds };
};

export const formatScannerError = ({ message, details }: { message: string; details?: string }) => {
  const parsedDetails = details ? parseDetails(details) : undefined;
  return parsedDetails ? `${message} (${parsedDetails})` : message;
};

export const getTransferBatchDescription = (): string => `Check Scan ${getFormattedDate(new Date(), 'mm-dd-yyyy')}`;

export const groupChecksByBankAccount = (
  checks: CreateCheckDepositScanner[]
): { [bankAccountId: string]: CreateCheckDepositScanner[] } => {
  const checksByBankAccount: { [bankAccountId: string]: CreateCheckDepositScanner[] } = {};

  for (const check of checks) {
    const bankAccountId = check.bankAccountId;
    if (!bankAccountId) {
      continue;
    }
    if (!checksByBankAccount[bankAccountId]) {
      checksByBankAccount[bankAccountId] = [];
    }
    checksByBankAccount[bankAccountId].push(check);
  }

  return checksByBankAccount;
};

export const createCheckDepositRequest = (checkData: CreateCheckDepositScanner): CheckDepositRequest => {
  const { auxiliaryOnUs, onUs } = mergeAccountAndCheckNumber(
    checkData.micrLine?.onUs ?? '',
    checkData.micrLine?.auxiliaryOnUs ?? '',
    checkData.accountNumber ?? '',
    checkData.checkNumber ?? ''
  );

  return {
    bankAccountId: checkData.bankAccountId,
    accountNumberId: checkData.accountNumberId,
    currencyCode: checkData.currencyCode,
    description: checkData.description,
    depositedAmount: checkData.depositedAmount,
    micrLine: {
      ...(checkData.micrLine ?? {}),
      auxiliaryOnUs,
      onUs,
    },
    imageFront: checkData.imageFront,
    imageBack: checkData.imageBack,
    transferBatchId: checkData.transferBatchId,
  };
};

const parseDetails = (details: string) => {
  // Details are typically formatted as "ERROR Type: Description"
  const detailsParts = details.split(':');
  if (detailsParts.length === 1) {
    // If there are not multiple parts, return the details as is
    return details;
  }

  return detailsParts.slice(1).join(':').trim();
};
